import React from 'react';
import { Link } from 'react-router-dom';
import { useFeatures, FEATURE_LABEL } from '../lib/features';
import { useMe } from '../lib/me';
import './FeatureRoute.css';

/**
 * A page behind a feature switch (migration 078, Settings -> Features).
 *
 * Somebody who follows a bookmark, an old email or the bell to a page whose
 * feature is switched off for them gets a calm page saying so - not a crash,
 * not a screen of refused requests, not a silent bounce to somewhere else
 * that leaves them wondering whether they mistyped. Nothing is said about
 * WHY beyond "your administrator", because the person reading it cannot
 * change it; an administrator is told where they can.
 *
 * While the answer is not yet known the page is not started at all - one
 * small request, and a switched-off page never begins loading its data.
 *
 * `feature` may be one key or several (Record in the room is Meetings AND
 * recording); the first one that is off is the one named.
 */
export function SwitchedOff({ feature }) {
  const me = useMe();
  const label = FEATURE_LABEL[feature] || 'This feature';
  return (
    <div className="container">
      <section className="fso" role="status" aria-live="polite">
        <span className="fso-ico" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3v8" />
            <path d="M6.3 6.6a8 8 0 1 0 11.4 0" />
          </svg>
        </span>
        <h1 className="fso-title">{label} is switched off</h1>
        <p className="fso-text">
          This feature is switched off by your administrator. Nothing has been
          deleted - everything will be here again when it is switched back on.
        </p>
        <div className="fso-actions">
          {me?.isAdmin && (
            <Link className="btn btn-primary" to="/settings/people/feature-access">Open Settings → Feature access</Link>
          )}
          <Link className={me?.isAdmin ? 'btn btn-secondary' : 'btn btn-primary'} to="/">Go to my home page</Link>
        </div>
      </section>
    </div>
  );
}

export default function FeatureRoute({ feature, children }) {
  const value = useFeatures();
  if (!value) {
    return <div className="container"><p className="fso-wait" aria-busy="true">Loading…</p></div>;
  }
  const keys = Array.isArray(feature) ? feature : [feature];
  const off = keys.find((k) => value.features[k] === false);
  if (off) return <SwitchedOff feature={off} />;
  return children;
}
